import { useEffect } from "react";
import type { GetLayoutFontFaces } from "./DataProviderContext.js";

const CSS_FONT_FORMAT_BY_EXTENSION = {
  woff2: "woff2",
  ttf: "truetype",
  otf: "opentype",
} as const;

const LOADED_FONT_KEYS = new Set<string>();

/**
 * Loads user layout fonts into `document.fonts` when a provider callback is available.
 * Safe to call from multiple renderers; each font family/url pair is loaded once per session.
 *
 * @param getLayoutFontFaces - Host callback listing custom fonts (API + auth), or undefined.
 */
export function useLayoutFontFacesLoader(
  getLayoutFontFaces: GetLayoutFontFaces | undefined,
): void {
  useEffect(() => {
    if (!getLayoutFontFaces) return;

    let cancelled = false;

    const loadFontFaces = async () => {
      try {
        const result = await getLayoutFontFaces();
        if (!result || cancelled) return;

        const normalizedApiUrl = result.apiUrl.replace(/\/+$/, "");
        for (const font of result.fonts) {
          if (cancelled) return;
          if (!font?.family || !font?.fontUrl) continue;

          const fontKey = `${font.family}::${font.fontUrl}`;
          if (LOADED_FONT_KEYS.has(fontKey)) continue;

          const normalizedFontUrl = font.fontUrl.startsWith("http")
            ? font.fontUrl
            : `${normalizedApiUrl}/${font.fontUrl.replace(/^\/+/, "")}`;
          const response = await fetch(normalizedFontUrl, {
            headers: result.token
              ? { Authorization: `Bearer ${result.token}` }
              : undefined,
          });
          if (!response.ok) continue;

          const fontBuffer = await response.arrayBuffer();
          const cssFormat = CSS_FONT_FORMAT_BY_EXTENSION[font.format];
          if (!cssFormat) continue;
          const fontFace = new FontFace(font.family, fontBuffer, {
            style: "normal",
          });
          await fontFace.load();
          if (cancelled) return;

          window.document.fonts.add(fontFace);
          LOADED_FONT_KEYS.add(fontKey);
        }
      } catch {
        // Avoid blocking layout rendering if custom font loading fails.
      }
    };

    void loadFontFaces();

    return () => {
      cancelled = true;
    };
  }, [getLayoutFontFaces]);
}
